import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import GoogleAnalytics from "./components/GoogleAnalytics";
import ErrorBoundary from "./components/ErrorBoundary";
import StickyContactBar from "./components/StickyContactBar";
import CookieConsent from "./components/CookieConsent";
import { initContactTracking } from "./lib/analytics";
import { initAttribution } from "./lib/attribution";
import { isInitialView } from "./lib/initialView";
import lazyWithPreload from "./lib/lazyWithPreload";

// Homepage se načítá hned (první dojem); ostatní stránky lazy –
// návštěvník nestahuje admin, rezervaci ani blog, dokud na ně nejde
import HomePage from "./pages/HomePage";
// VoucherPage (landing z kampaní) je lazy jako ostatní, ale index.js její kód
// stahuje hned při vstupu (preloadRoute) ze stejného nasazení jako HTML –
// chybějící chunk po novém nasazení jí tak nehrozí.
const VoucherPage = lazyWithPreload(() => import("./pages/VoucherPage"));
// Až po startu: popup i toasty nejsou potřeba pro první vykreslení a jen by
// zvětšovaly hlavní balíček (sonner, formulářové prvky)
const EmailPopup = lazy(() => import("./components/EmailPopup"));
const Toaster = lazy(() => import("./components/ui/sonner").then((m) => ({ default: m.Toaster })));
const ServicesPage = lazyWithPreload(() => import("./pages/ServicesPage"));
const PricingPage = lazyWithPreload(() => import("./pages/PricingPage"));
const BookingPage = lazyWithPreload(() => import("./pages/BookingPage"));
const AboutPage = lazyWithPreload(() => import("./pages/AboutPage"));
const ContactPage = lazyWithPreload(() => import("./pages/ContactPage"));
const AdminPage = lazyWithPreload(() => import("./pages/AdminPage"));
const GalleryPage = lazyWithPreload(() => import("./pages/GalleryPage"));
const GalleryDetailPage = lazyWithPreload(() => import("./pages/GalleryDetailPage"));
const BlogListPage = lazyWithPreload(() => import("./pages/BlogPage").then(m => ({ default: m.BlogListPage })));
const BlogDetailPage = lazyWithPreload(() => import("./pages/BlogPage").then(m => ({ default: m.BlogDetailPage })));
const LocalLandingPage = lazyWithPreload(() => import("./pages/LocalLandingPage"));
const ServiceLandingPage = lazyWithPreload(() => import("./pages/ServiceLandingPage"));
const TopicLandingPage = lazyWithPreload(() => import("./pages/TopicLandingPage"));
const GdprPage = lazyWithPreload(() => import("./pages/LegalPage").then(m => ({ default: m.GdprPage })));
const TermsPage = lazyWithPreload(() => import("./pages/LegalPage").then(m => ({ default: m.TermsPage })));
const NotFoundPage = lazyWithPreload(() => import("./pages/NotFoundPage"));

// Stránky služeb a měst sdílejí šablonu – seznam drží routy i přednačtení v souladu
const SERVICE_LANDINGS = [
  'strihani-keru-kaceni-stromu', 'realizace-zahrad', 'pokladani-travniku', 'likvidace-pozemku',
  'sekani-prerostle-travy', 'vertikutace-travniku', 'udrzba-zahrady', 'odvoz-bioodpadu',
];
const CITY_LANDINGS = ['hradec-kralove', 'trutnov', 'vrchlabi', 'jaromer', 'nachod', 'hostinne', 'horice', 'jicin',
  'ceska-skalice', 'nova-paka', 'upice', 'hronov', 'smirice'];
// Tematické stránky (obsah v data/topicPages.js)
const TOPIC_LANDINGS = [
  'travni-koberec-hradec-kralove', 'strihani-tuji-cena', 'kaceni-stromu', 'cisteni-zahrady',
  'sekani-travy-cena', 'pravidelne-sekani-travy', 'zahradnik-dvur-kralove',
  'hrabani-listi', 'uklid-snehu', 'rez-ovocnych-stromu', 'udrzba-zelene-pro-firmy',
];

const ROUTE_CHUNKS = [
  [/^\/sluzby$/, ServicesPage],
  [/^\/cenik$/, PricingPage],
  [/^\/rezervace$/, BookingPage],
  [/^\/o-nas$/, AboutPage],
  [/^\/kontakt$/, ContactPage],
  [/^\/admin/, AdminPage],
  [/^\/nase-prace$/, GalleryPage],
  [/^\/nase-prace\/./, GalleryDetailPage],
  [/^\/blog$/, BlogListPage],
  [/^\/blog\/./, BlogDetailPage],
  [/^\/sekani-travy-/, LocalLandingPage],
  [new RegExp(`^/(${SERVICE_LANDINGS.join('|')})$`), ServiceLandingPage],
  [new RegExp(`^/(${TOPIC_LANDINGS.join('|')})$`), TopicLandingPage],
  [/^\/poukaz\//, VoucherPage],
  [/^\/gdpr$/, GdprPage],
  [/^\/obchodni-podminky$/, TermsPage],
];

// Stáhne kód stránky pro danou adresu (viz lib/lazyWithPreload.js).
// Homepage je v hlavním balíčku, tam není co stahovat.
export function preloadRoute(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const hit = ROUTE_CHUNKS.find(([re]) => re.test(path));
  return hit ? hit[1].preload() : Promise.resolve();
}

// Globální nastavení axiosu. Axios se stahuje jako samostatný kus až se
// stránkami, které volají API (homepage ho nepotřebuje). Instance je sdílená,
// takže nastavení platí i pro importy ve stránkách.
import("axios").then(({ default: axios }) => {
  axios.defaults.timeout = 15000;
  // Globální zachycení chyb sítě / CORS – log pro debugging
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!error.response) {
        console.error('[API] Network/CORS error:', error.message, 'URL:', error.config?.url);
      }
      return Promise.reject(error);
    }
  );
});

// Po změně routy odscrollovat nahoru
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Plynulý nástup obsahu při přechodu mezi stránkami
const PageFade = ({ children }) => {
  const { pathname } = useLocation();
  return (
    // Na vstupní stránce bez animace – viz lib/initialView.js
    <div key={pathname} className={isInitialView(pathname) ? undefined : 'page-enter'}>
      {children}
    </div>
  );
};

// Na /rezervace schováváme patičku i e-mailový popup – formulář se má vejít
// na jednu obrazovku a cokoli pod ním by vynutilo scroll.
const SiteChrome = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/rezervace')) return null;
  return (
    <>
      <Footer />
      <Suspense fallback={null}><EmailPopup /></Suspense>
    </>
  );
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-[#3FA34D] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  // Jeden delegovaný listener na tel:/wa.me/mailto odkazy v celé aplikaci
  // + zachycení původu návštěvy (gclid, utm_*) hned při vstupu na web
  useEffect(() => { initContactTracking(); initAttribution(); }, []);

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <GoogleAnalytics />
        <Analytics />
        <SpeedInsights />
        <ScrollToTop />
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Admin - no header/footer */}
          <Route path="/admin" element={<AdminPage />} />

          {/* Public site */}
          <Route path="/*" element={
            <>
              <Header />
              <main>
                <PageFade>
                {/* Vlastní Suspense: při stahování kódu podstránky zůstane hlavička i patička */}
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/sluzby" element={<ServicesPage />} />
                  <Route path="/cenik" element={<PricingPage />} />
                  <Route path="/rezervace" element={<BookingPage />} />
                  <Route path="/o-nas" element={<AboutPage />} />
                  <Route path="/kontakt" element={<ContactPage />} />
                  <Route path="/poukaz/:code" element={<VoucherPage />} />
                  <Route path="/nase-prace" element={<GalleryPage />} />
                  <Route path="/nase-prace/:slug" element={<GalleryDetailPage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<BlogDetailPage />} />
                  {CITY_LANDINGS.map((slug) => (
                    <Route key={slug} path={`/sekani-travy-${slug}`} element={<LocalLandingPage citySlug={slug} />} />
                  ))}
                  {SERVICE_LANDINGS.map((slug) => (
                    <Route key={slug} path={`/${slug}`} element={<ServiceLandingPage serviceSlug={slug} />} />
                  ))}
                  {TOPIC_LANDINGS.map((slug) => (
                    <Route key={slug} path={`/${slug}`} element={<TopicLandingPage slug={slug} />} />
                  ))}
                  <Route path="/gdpr" element={<GdprPage />} />
                  <Route path="/obchodni-podminky" element={<TermsPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </Suspense>
                </PageFade>
              </main>
              <SiteChrome />
              <WhatsAppButton />
              <StickyContactBar />
              <CookieConsent />
            </>
          } />
        </Routes>
        <Suspense fallback={null}><Toaster position="top-center" richColors /></Suspense>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
